import "../styles/globals.css";
import "../styles/newspaper-styles.css"; 
import "../styles/neo-civic-noir.css";
import "../styles/planning-register.css";
import "../styles/fonts.css";
import "../styles/crt-effect.css";
import "../styles/settings.css";
// Import view tracker to initialize developer utilities
import '../lib/viewTracker';
import dynamic from "next/dynamic";
import Script from "next/script";
import { useEffect, useState, useRef } from "react";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../components/ui/use-toast";
import { useRouter } from 'next/router';
import { ThemeProvider } from 'next-themes';
import SettingsStyleProvider from '../components/settings/SettingsStyleProvider';
import UnifiedThemeProvider from '../components/UnifiedThemeProvider';
import { getCouncilConfig } from '../lib/councils';
import { isPlanningFeedRoute } from '../lib/planning/feedRoute';
import '../utils/silenceConsole';
import { createPortal } from 'react-dom';

// Dynamically import Navbar to avoid SSR issues
const Navbar = dynamic(() => import("../components/Navbar"), { ssr: false });
const PlanningAreaSelector = dynamic(() => import("../components/planning/PlanningAreaSelector"), { ssr: false });
const ConfigureAmplifyClient = dynamic(() => import('../components/ConfigureAmplifyClient'), { ssr: false });
const WelcomeBackAccountModal = dynamic(() => import('../components/account/WelcomeBackAccountModal'), { ssr: false });
const BackgroundProcessingIndicator = dynamic(() => import('../components/ui/BackgroundProcessingIndicator'), { ssr: false });
const IconCacheStatus = dynamic(() => import('../components/ui/IconCacheStatus'), { ssr: false });
const PlanningDocumentViewerModal = dynamic(() => import('../components/planning/PlanningDocumentViewerModal'), { ssr: false });
const CityScopeClientErrorTracker = dynamic(() => import("../components/analytics/CityScopeClientErrorTracker"), { ssr: false });
const CityScopeJourneyTracker = dynamic(() => import("../components/analytics/CityScopeJourneyTracker"), { ssr: false });
const MatomoPagesRouteTracker = dynamic(() => import("../components/analytics/MatomoPagesRouteTracker"), { ssr: false });
const VercelAnalytics = dynamic(
  () => import("@vercel/analytics/next").then((module) => module.Analytics),
  { ssr: false }
);
const VercelSpeedInsights = dynamic(
  () => import("@vercel/speed-insights/next").then((module) => module.SpeedInsights),
  { ssr: false }
);

// Show cache status in development - disabled by request
const SHOW_CACHE_STATUS = false;
const GA_MEASUREMENT_ID = 'G-2E294WL99R';
const GA_TRACKING_ENABLED =
  process.env.NEXT_PUBLIC_GA_TRACKING_ENABLED === '1' ||
  process.env.NEXT_PUBLIC_GA_TRACKING_ENABLED === 'true';
const DEMO_MODE_ENABLED = process.env.NEXT_PUBLIC_DEMO_MODE === '1';
const ENABLE_LOCAL_OBSERVABILITY = process.env.NEXT_PUBLIC_ENABLE_LOCAL_OBSERVABILITY === '1';
const AUTH_EAGER_ROUTE_PREFIXES = [
  '/auth',
  '/user',
  '/users',
  '/reports/signup',
  '/settings',
  '/planning-updates',
  '/planning-alerts',
  '/planning-map',
  '/planning-application',
  '/agent-studio',
];
const PLANNING_ICON_ROUTE_PREFIXES = ['/planning-updates', '/planning-map', '/planning-application'];
const PLANNING_UPDATES_LOADER_EVENT = 'planning-updates-loader';
const PLANNING_UPDATES_LOADER_STATE_KEY = '__cityScopePlanningUpdatesLoaderState';

const scheduleBrowserIdleTask = (callback, timeout = 2500) => {
  if (typeof window === 'undefined') return () => {};

  let cancelled = false;
  let delayId = null;
  let idleId = null;
  const run = () => {
    if (cancelled) return;
    callback();
  };

  delayId = window.setTimeout(() => {
    if (cancelled) return;

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 1000 });
      return;
    }

    run();
  }, timeout);

  return () => {
    cancelled = true;
    if (idleId !== null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(idleId);
    }
    if (delayId !== null) {
      window.clearTimeout(delayId);
    }
  };
};

const isLocalObservabilityHost = () =>
  typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname);

const shouldEnableBrowserObservability = () =>
  !isLocalObservabilityHost() || ENABLE_LOCAL_OBSERVABILITY;

const createSlowRouteOverlayState = (overrides = {}) => ({
  active: false,
  targetUrl: '',
  progress: 0,
  stage: 'connecting',
  title: '',
  description: '',
  loadedCount: 0,
  totalCount: 0,
  ...overrides,
});

const normalizeRouteForOverlay = (url = '') => {
  const raw = String(url || '').trim();
  if (!raw) return '';

  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.city-scope.co.uk';
    const parsed = new URL(raw, base);
    return `${parsed.pathname}${parsed.search || ''}`;
  } catch (_) {
    return raw;
  }
};

const isSlowRoute = (url = '') => {
  return isPlanningFeedRoute(url);
};

const isPlanningRegionRoute = (url = '') => {
  const normalized = normalizeRouteForOverlay(url);
  const pathOnly = normalized.split('?')[0].split('#')[0];

  if (pathOnly === '/planning-map' || pathOnly.startsWith('/planning-map/')) {
    return true;
  }
  if (pathOnly === '/planning-updates') return true;
  if (!pathOnly.startsWith('/planning-updates/')) return false;

  const routeParts = pathOnly.split('/').filter(Boolean);
  return routeParts.length === 2 && Boolean(getCouncilConfig(routeParts[1]));
};

const isAuthEagerRoute = (url = '') => {
  const normalized = normalizeRouteForOverlay(url);
  const pathOnly = normalized.split('?')[0];
  return AUTH_EAGER_ROUTE_PREFIXES.some((prefix) => pathOnly.startsWith(prefix));
};

const getPlanningIconPreloadDelay = (url = '') => {
  const normalized = normalizeRouteForOverlay(url);
  const pathOnly = normalized.split('?')[0];
  if (pathOnly.startsWith('/planning-updates')) return null;
  if (PLANNING_ICON_ROUTE_PREFIXES.some((prefix) => pathOnly.startsWith(prefix))) return 4500;
  return null;
};

const getCurrentBrowserRoute = () => {
  if (typeof window === 'undefined') return '';
  const { pathname = '', search = '' } = window.location || {};
  return `${pathname}${search || ''}`;
};

const shouldLoadAmplifyClientInitially = () => {
  if (typeof window === 'undefined') return false;
  return isAuthEagerRoute(getCurrentBrowserRoute());
};

const getInitialObservabilityDelay = (url = '') => {
  const normalized = normalizeRouteForOverlay(url);
  const pathOnly = normalized.split('?')[0];
  if (pathOnly === '/') return 5000;
  if (pathOnly.startsWith('/planning-updates')) return 12000;
  if (
    pathOnly.startsWith('/planning-map')
    || pathOnly.startsWith('/planning-application')
    || pathOnly === '/edi-tram-plan'
    || pathOnly === '/leeds-tram-plan'
    || pathOnly === '/tram-plan'
  ) {
    return 8000;
  }
  return 0;
};

function SlowRouteTransitionOverlay({
  active,
  targetUrl,
  progress = 0,
  stage = 'connecting',
  title: explicitTitle,
  description: explicitDescription,
  loadedCount = 0,
  totalCount = 0,
}) {
  if (!active) return null;
  if (typeof document === 'undefined') return null;

  const normalizedTarget = normalizeRouteForOverlay(targetUrl);
  const isPlanningUpdatesTarget = normalizedTarget.startsWith('/planning-updates');
  const boundedProgress = Math.max(0, Math.min(100, Number(progress) || 0));
  const showDeterminateProgress =
    Number(totalCount) > 0 ||
    Number(loadedCount) > 0 ||
    stage === 'hydrating' ||
    stage === 'ready';
  const progressLabel = showDeterminateProgress ? `${Math.round(boundedProgress)}%` : 'Loading';
  const title = (
    explicitTitle ||
    (isPlanningUpdatesTarget
      ? 'Preparing Planning Updates'
      : 'Loading')
  );
  const description = explicitDescription || (
    isPlanningUpdatesTarget
      ? (showDeterminateProgress
        ? 'Loading the dashboard shell and warming up the wider planning feed.'
        : 'Opening the dashboard shell and requesting the latest planning records.')
      : 'Loading the next page.'
  );
  const steps = [
    {
      label: 'Connection',
      status: boundedProgress >= 20 ? 'done' : stage === 'connecting' || stage === 'routing' ? 'active' : 'pending',
    },
    {
      label: 'Records',
      status:
        boundedProgress >= 65
          ? 'done'
          : stage === 'bootstrapping' || stage === 'hydrating'
          ? 'active'
          : 'pending',
    },
    {
      label: 'Dashboard',
      status: boundedProgress >= 100 ? 'done' : stage === 'ready' ? 'active' : 'pending',
    },
  ];
  const countSummary = !showDeterminateProgress
    ? stage === 'routing'
      ? 'Opening the planning dashboard shell'
      : stage === 'bootstrapping'
      ? 'Preparing the first planning records'
      : 'Requesting the latest planning feed'
    : totalCount > 0
    ? `${Number(loadedCount || 0).toLocaleString()} / ${Number(totalCount).toLocaleString()} applications ready`
    : loadedCount > 0
    ? `${Number(loadedCount).toLocaleString()} applications ready`
    : 'Waiting for the planning feed to respond';
  const overlayMarkup = (
    <div
      className="planning-register fixed inset-0 z-[6000] flex items-center justify-center bg-[rgba(var(--c-bg-rgb),0.94)] text-[var(--c-text-primary)]"
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      aria-label={title}
    >
      <div className="pr-route-overlay mx-4 w-full max-w-lg border border-[var(--c-border)] bg-[var(--c-surface)]">
        <hr className="pr-double-rule" aria-hidden="true" />
        <div className="flex flex-col gap-6 p-7">
          <p className="pr-kicker pr-kicker--accent">CityScope</p>

          <div className="space-y-2">
            <h1 className="font-['Newsreader'] text-[clamp(1.75rem,4vw,2rem)] font-medium leading-tight tracking-[-0.01em] text-[var(--c-text-primary)]">
              {title}
            </h1>
            <p className="max-w-md text-sm leading-relaxed text-[var(--c-text-secondary)] sm:text-[15px]">
              {description}
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between font-['DM_Mono'] text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
              <span>Loading progress</span>
              <span className="text-[var(--c-accent)]">{progressLabel}</span>
            </div>

            {showDeterminateProgress ? (
              <div className="h-px w-full bg-[var(--c-border)]">
                <div
                  className="h-full bg-[var(--c-accent)] transition-[width] duration-500 ease-out"
                  style={{ width: `${boundedProgress}%` }}
                />
              </div>
            ) : (
              <div className="h-px w-full overflow-hidden bg-[var(--c-border)]">
                <div className="pr-map-loading-rule h-full w-1/3 bg-[var(--c-accent)]" />
              </div>
            )}

            <div className="pr-panel px-3 py-3 font-['DM_Mono'] text-[11px] leading-relaxed text-[var(--c-text-secondary)]">
              {countSummary}
            </div>

            <div className="pr-segment w-full" role="list" aria-label="Loading stages">
              {steps.map((step) => {
                const isActive = step.status === 'active';
                const isDone = step.status === 'done';

                return (
                  <div
                    key={step.label}
                    role="listitem"
                    aria-current={isActive ? 'step' : undefined}
                    className={`pr-segment-item flex-1 justify-center text-center ${
                      isDone
                        ? 'bg-[var(--c-accent-muted)] text-[var(--c-text-primary)]'
                        : isActive
                        ? 'pr-loader-step-active'
                        : ''
                    }`}
                  >
                    <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.1em]">
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlayMarkup, document.body);
}

// GlobalToastListener updated for new toast system
function GlobalToastListener() {
  useEffect(() => {
    if (DEMO_MODE_ENABLED) return;

    function handleToastEvent(e) {
      toast({
        title: e.detail.title,
        description: e.detail.description,
        variant: e.detail.variant
      });
    }
    window.addEventListener("cityscope-toast", handleToastEvent);
    return () => {
      window.removeEventListener("cityscope-toast", handleToastEvent);
    };
  }, []);

  return null;
}

// Global error handler for authentication errors
function GlobalErrorHandler() {
  useEffect(() => {
    function handleUnhandledRejection(event) {
      const error = event.reason;
      
      // Handle authentication errors silently
      if (error?.message === 'Not authenticated' || 
          error?.message === 'Authentication session unavailable' ||
          error?.status === 401) {
        console.debug('Authentication error handled globally - user likely signed out');
        event.preventDefault(); // Prevent the error from being logged to console
        return;
      }
      
      // Handle other API errors silently
      if (error?.name === 'AccountsApiError') {
        console.debug('API error handled globally:', error.message);
        event.preventDefault();
        return;
      }
    }
    
    function handleError(event) {
      const error = event.error;
      
      // Handle authentication errors silently
      if (error?.message === 'Not authenticated' || 
          error?.message === 'Authentication session unavailable' ||
          error?.status === 401) {
        console.debug('Authentication error handled globally - user likely signed out');
        event.preventDefault();
        return;
      }
    }
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return null;
}

function MyApp({ Component, pageProps }) {
  const scrollContainerRef = useRef(null); 
  const [isVisible, setIsVisible] = useState(true); 
  const lastScrollYRef = useRef(0);
  const [browserObservabilityEnabled, setBrowserObservabilityEnabled] = useState(false);
  const [shouldLoadAmplifyClient, setShouldLoadAmplifyClient] = useState(shouldLoadAmplifyClientInitially);
  const [shouldLoadWelcomeBackModal, setShouldLoadWelcomeBackModal] = useState(false);
  const [slowRouteOverlay, setSlowRouteOverlay] = useState(() => createSlowRouteOverlayState());
  const navbarHeight = 80; 
  const router = useRouter();
  const currentRouteRef = useRef(router.asPath || '');
  const slowRouteDismissTimeoutRef = useRef(null);
  const slowRouteOverlayArmedRef = useRef(false);
  const slowRouteOverlayRef = useRef(slowRouteOverlay);
  const planningLoaderFallbackTimeoutRef = useRef(null);
  const isDirectPlanningApplicationRoute = router.pathname === '/planning-application/[...reference]'
    || router.pathname === '/[...reference]'
    || (typeof router.asPath === 'string' && router.asPath.startsWith('/planning-application/'));
  // When non-null, navbar visibility is forced (e.g., while a modal is open)
  const navbarForcedVisibilityRef = useRef(null);

  // Background categorisation processing indicator state
  const [isCategorizationProcessing, setIsCategorizationProcessing] = useState(false);
  
  // Icon cache status monitoring (development only)
  const [showCacheStatus, setShowCacheStatus] = useState(SHOW_CACHE_STATUS);

  useEffect(() => {
    if (browserObservabilityEnabled || !shouldEnableBrowserObservability()) return undefined;

    const delay = getInitialObservabilityDelay(router.asPath || router.pathname || '');
    if (delay <= 0) {
      setBrowserObservabilityEnabled(true);
      return undefined;
    }

    return scheduleBrowserIdleTask(() => setBrowserObservabilityEnabled(true), delay);
  }, [browserObservabilityEnabled, router.asPath, router.pathname]);

  useEffect(() => {
    if (shouldLoadAmplifyClient) return undefined;

    const currentRoute = router.asPath || router.pathname || '';
    if (isAuthEagerRoute(currentRoute)) {
      setShouldLoadAmplifyClient(true);
      return undefined;
    }

    return scheduleBrowserIdleTask(() => setShouldLoadAmplifyClient(true), 3500);
  }, [router.asPath, router.pathname, shouldLoadAmplifyClient]);

  useEffect(() => {
    if (DEMO_MODE_ENABLED || shouldLoadWelcomeBackModal) return undefined;

    const currentRoute = router.asPath || router.pathname || '';
    const delay = isAuthEagerRoute(currentRoute) ? 2500 : 6500;

    return scheduleBrowserIdleTask(() => setShouldLoadWelcomeBackModal(true), delay);
  }, [router.asPath, router.pathname, shouldLoadWelcomeBackModal]);

  useEffect(() => {
    // Note: Favorites hydration is now handled by individual components (PlanningContext, UserPage)
    // to prevent duplicate API calls and race conditions. Removed from _app.js.
    
    // Removed emergency redirect that rewrote /planning-updates/<ref> to ?ref
    // to avoid fighting client-side routing and history state.
    
    const isLocalhost = typeof window !== 'undefined'
      && ['localhost', '127.0.0.1'].includes(window.location.hostname);

    // Development safety: if a service worker was previously registered on this origin
    // (e.g. you ran `next start` on :3000 and later switched back to `next dev`),
    // it can keep serving cached assets/data and make desktop/phone look different.
    if ('serviceWorker' in navigator && (process.env.NODE_ENV !== 'production' || isLocalhost)) {
      try {
        navigator.serviceWorker
          .getRegistrations()
          .then((regs) => Promise.all(regs.map((reg) => reg.unregister())))
          .catch(() => {});
      } catch (_) {
        // ignore
      }

      try {
        if (window.caches?.keys) {
          window.caches
            .keys()
            .then((keys) =>
              Promise.all(
                keys
                  .filter((key) => key.startsWith('city-scope-'))
                  .map((key) => window.caches.delete(key))
              )
            )
            .catch(() => {});
        }
      } catch (_) {
        // ignore
      }
    }

    // Register service worker for PWA support
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production' && !isLocalhost) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch(err => {
          console.error('Service worker registration failed:', err);
        });
      });
    }

    // Optional: Listen for icon preload completion
    const handleIconsPreloaded = (event) => {
      const { successful, total } = event.detail;
      console.log(`🎯 Planning icons ready: ${successful}/${total} icons cached successfully`);
      
      // Automatically hide cache status after successful completion in development
      if (SHOW_CACHE_STATUS && successful === total) {
        setTimeout(() => {
          setShowCacheStatus(false);
        }, 3000);
      }
    };

    window.addEventListener('icons-preloaded', handleIconsPreloaded);

    // Development keyboard shortcut to toggle cache status (Ctrl+Shift+C)
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'C' && SHOW_CACHE_STATUS) {
        setShowCacheStatus(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('icons-preloaded', handleIconsPreloaded);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const currentRoute = router.asPath || router.pathname || '';
    const preloadDelay = getPlanningIconPreloadDelay(currentRoute);
    if (preloadDelay === null) return undefined;

    return scheduleBrowserIdleTask(async () => {
      try {
        const { iconCacheService } = await import('../lib/services/iconCacheService');
        await iconCacheService.preloadAllIcons();
      } catch (error) {
        console.error('Failed to preload planning icons:', error);
      }
    }, preloadDelay);
  }, [router.asPath, router.pathname]);

  // Expose the live navbar height as a CSS var so map overlays can reliably offset
  // below the fixed header (and iOS safe-area padding handled inside .noir-nav).
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;

    let navbarEl = null;
    let resizeObserver;
    let rafId;

    const update = () => {
      if (!navbarEl) return;
      try {
        const rect = navbarEl.getBoundingClientRect();
        const height = Math.max(0, Math.round(rect.height));
        root.style.setProperty('--navbar-height', `${height}px`);
        root.style.setProperty('--cs-nav-height', `${height}px`);
      } catch (_) {
        // ignore
      }
    };

    const ensureNavbar = () => {
      navbarEl = document.querySelector('.noir-nav');
      if (!navbarEl) {
        rafId = window.requestAnimationFrame(ensureNavbar);
        return;
      }

      update();

      if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
        try {
          resizeObserver = new ResizeObserver(() => update());
          resizeObserver.observe(navbarEl);
        } catch (_) {
          // ignore
        }
      }

      window.addEventListener('resize', update);
    };

    ensureNavbar();

    return () => {
      try {
        if (rafId) window.cancelAnimationFrame(rafId);
      } catch (_) {
        // ignore
      }
      window.removeEventListener('resize', update);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Optimized scroll handler using requestAnimationFrame and passive event
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    
    let ticking = false;
    const container = scrollContainerRef.current;
    
    const controlNavbar = () => {
      // Respect forced visibility (e.g., planning modal open)
      if (navbarForcedVisibilityRef.current !== null) {
        // Keep navbar in the forced state
        setIsVisible((previous) =>
          previous === navbarForcedVisibilityRef.current
            ? previous
            : navbarForcedVisibilityRef.current
        );
        ticking = false;
        return;
      }
      // The container grows with its content, so in practice the document
      // (window) is what scrolls. Read both so the navbar hides on phones.
      const currentScrollY = Math.max(container.scrollTop, window.scrollY || 0);
      const lastScrollY = lastScrollYRef.current;
      
      if (currentScrollY > lastScrollY && currentScrollY > navbarHeight) {
        // Scrolling down past threshold -> Hide
        setIsVisible((previous) => (previous ? false : previous));
      } else if (currentScrollY < lastScrollY || currentScrollY <= navbarHeight) {
        // Scrolling up OR near top -> Show
        setIsVisible((previous) => (previous ? previous : true));
      }
      
      lastScrollYRef.current = currentScrollY;
      
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(controlNavbar);
        ticking = true;
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Expose how much of the top edge the navbar currently covers, so sticky page
  // headers can sit flush under it and slide up when it auto-hides on scroll.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(
      '--navbar-visible-height',
      isVisible ? 'var(--navbar-height, 64px)' : '0px'
    );
  }, [isVisible]);

  // Direct application detail pages use the inline register record with global nav.
  useEffect(() => {
    if (isDirectPlanningApplicationRoute) {
      navbarForcedVisibilityRef.current = true;
      setIsVisible(true);
      return;
    }

    if (navbarForcedVisibilityRef.current === true) {
      navbarForcedVisibilityRef.current = null;
    }
  }, [isDirectPlanningApplicationRoute]);

  useEffect(() => {
    const onNavbarVisibility = (e) => {
      try {
        const detail = e?.detail || {};
        const { visible, lock } = detail;
        
        if (typeof visible === 'boolean') {
          setIsVisible(visible);
        }
        if (typeof lock === 'boolean') {
          navbarForcedVisibilityRef.current = lock ? (typeof visible === 'boolean' ? visible : isVisible) : null;
        }
      } catch (_) {
        // Silently handle errors
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('navbar-visibility', onNavbarVisibility);
      return () => window.removeEventListener('navbar-visibility', onNavbarVisibility);
    }
  }, [isVisible]);

  // Performance optimization for route changes
  useEffect(() => {
    const handleRouteChange = () => {
      // Reset scroll position when route changes
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo(0, 0);
      }
      if (router.pathname === '/planning-application/[...reference]'
        || router.pathname === '/[...reference]'
        || (typeof router.asPath === 'string' && router.asPath.startsWith('/planning-application/'))) {
        navbarForcedVisibilityRef.current = true;
        setIsVisible(true);
      } else {
        // Always restore navbar visibility on route change outside dedicated modal pages
        navbarForcedVisibilityRef.current = null;
        setIsVisible(true);
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    currentRouteRef.current = router.asPath || '';
  }, [router.asPath]);

  useEffect(() => {
    slowRouteOverlayRef.current = slowRouteOverlay;
  }, [slowRouteOverlay]);

  useEffect(() => {
    const clearSlowRouteDismissTimeout = () => {
      if (slowRouteDismissTimeoutRef.current) {
        clearTimeout(slowRouteDismissTimeoutRef.current);
        slowRouteDismissTimeoutRef.current = null;
      }
    };

    const hideSlowRouteOverlay = () => {
      clearSlowRouteDismissTimeout();
      slowRouteOverlayArmedRef.current = false;
      setSlowRouteOverlay(createSlowRouteOverlayState());
    };

    const handleRouteChangeStart = (url, { shallow } = {}) => {
      if (shallow) return;

      const currentRoute = currentRouteRef.current || router.asPath || '';
      const browserRoute = getCurrentBrowserRoute();
      const alreadyOnSlowRoute =
        isSlowRoute(currentRoute) ||
        isSlowRoute(browserRoute) ||
        router.pathname === '/planning-updates';

      if (isSlowRoute(url) && !alreadyOnSlowRoute) {
        clearSlowRouteDismissTimeout();
        slowRouteOverlayArmedRef.current = true;
        setSlowRouteOverlay(createSlowRouteOverlayState({
          active: true,
          targetUrl: url,
          progress: 8,
          stage: 'routing',
          title: 'Preparing Planning Updates',
          description: 'Opening the dashboard shell and connecting to the latest planning feed.',
        }));
      }
    };

    const handleRouteChangeComplete = (url) => {
      currentRouteRef.current = typeof url === 'string' ? url : (router.asPath || currentRouteRef.current || '');
      if (!isSlowRoute(url)) {
        hideSlowRouteOverlay();
        return;
      }
      setSlowRouteOverlay((previous) => {
        if (!previous.active) return previous;
        return {
          ...previous,
          active: true,
          targetUrl: url,
          progress: Math.max(previous.progress || 0, 34),
          stage: 'bootstrapping',
          title: 'Loading Planning Updates',
          description: 'Building the planning dashboard and syncing the latest feed.',
        };
      });
      slowRouteDismissTimeoutRef.current = setTimeout(() => {
        hideSlowRouteOverlay();
      }, 45000);
    };

    const handleRouteChangeError = (_error, url) => {
      clearSlowRouteDismissTimeout();
      slowRouteOverlayArmedRef.current = false;
      currentRouteRef.current = typeof url === 'string' ? url : (router.asPath || currentRouteRef.current || '');
      hideSlowRouteOverlay();
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      clearSlowRouteDismissTimeout();
      slowRouteOverlayArmedRef.current = false;
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router]);

  useEffect(() => {
    const clearSlowRouteDismissTimeout = () => {
      if (slowRouteDismissTimeoutRef.current) {
        clearTimeout(slowRouteDismissTimeoutRef.current);
        slowRouteDismissTimeoutRef.current = null;
      }
    };

    const hideSlowRouteOverlay = () => {
      clearSlowRouteDismissTimeout();
      if (planningLoaderFallbackTimeoutRef.current) {
        clearTimeout(planningLoaderFallbackTimeoutRef.current);
        planningLoaderFallbackTimeoutRef.current = null;
      }
      slowRouteOverlayArmedRef.current = false;
      setSlowRouteOverlay(createSlowRouteOverlayState());
    };

    const handlePlanningUpdatesLoader = (event) => {
      // A feed can finish a request after navigation. Its loader must not cover
      // an application detail page, which never emits feed-ready events.
      if (!isSlowRoute(getCurrentBrowserRoute() || currentRouteRef.current || router.asPath)) {
        hideSlowRouteOverlay();
        return;
      }
      const detail = event?.detail || {};
      const incomingRoute = normalizeRouteForOverlay(detail?.route || detail?.targetUrl || '');
      const isPlanningUpdatesEvent = isSlowRoute(incomingRoute);

      if (!slowRouteOverlayArmedRef.current && !slowRouteOverlayRef.current.active && !isPlanningUpdatesEvent) {
        return;
      }

      clearSlowRouteDismissTimeout();
      if (planningLoaderFallbackTimeoutRef.current) {
        clearTimeout(planningLoaderFallbackTimeoutRef.current);
        planningLoaderFallbackTimeoutRef.current = null;
      }

      const nextProgress = Math.max(0, Math.min(100, Number(detail.progress) || 0));
      const nextLoadedCount = Math.max(0, Number(detail.loadedCount) || 0);
      const nextTotalCount = Math.max(0, Number(detail.totalCount) || 0);
      const nextStage = typeof detail.stage === 'string' ? detail.stage : 'bootstrapping';
      const nextTitle = typeof detail.title === 'string' ? detail.title : 'Loading Planning Updates';
      const isDoneEvent = detail.done === true;
      const nextDescription =
        typeof detail.description === 'string'
          ? detail.description
          : 'Building the planning dashboard and syncing the latest feed.';

      if (detail.active) {
        const nextState = {
          active: true,
          targetUrl: '/planning-updates',
          progress: Math.max(Number(slowRouteOverlayRef.current?.progress) || 0, nextProgress, 12),
          stage: nextStage,
          title: nextTitle,
          description: nextDescription,
          loadedCount: Math.max(Number(slowRouteOverlayRef.current?.loadedCount) || 0, nextLoadedCount),
          totalCount: Math.max(Number(slowRouteOverlayRef.current?.totalCount) || 0, nextTotalCount),
        };

        slowRouteOverlayArmedRef.current = true;
        setSlowRouteOverlay(nextState);

        if (!isDoneEvent && nextProgress >= 98 && nextProgress < 100) {
          planningLoaderFallbackTimeoutRef.current = setTimeout(() => {
            setSlowRouteOverlay((previous) => {
              if (!previous.active) return previous;
              return {
                ...previous,
                progress: 100,
                stage: 'ready',
                title: nextTitle || 'Planning Updates Ready',
                description: nextDescription || 'Opening the planning dashboard.',
              };
            });

            if (planningLoaderFallbackTimeoutRef.current) {
              clearTimeout(planningLoaderFallbackTimeoutRef.current);
              planningLoaderFallbackTimeoutRef.current = null;
            }

            slowRouteDismissTimeoutRef.current = setTimeout(() => {
              hideSlowRouteOverlay();
            }, 220);
          }, 1400);
        }

        return;
      }

      if (planningLoaderFallbackTimeoutRef.current) {
        clearTimeout(planningLoaderFallbackTimeoutRef.current);
        planningLoaderFallbackTimeoutRef.current = null;
      }

      setSlowRouteOverlay((previous) => {
        if (!previous.active && !slowRouteOverlayArmedRef.current) {
          return previous;
        }

        return {
          ...previous,
          active: true,
          targetUrl: '/planning-updates',
          progress: isDoneEvent ? 100 : Math.max(Number(previous.progress) || 0, nextProgress, 12),
          stage: 'ready',
          title: nextTitle || 'Planning Updates Ready',
          description: nextDescription || 'Opening the planning dashboard.',
          loadedCount: Math.max(Number(previous.loadedCount) || 0, nextLoadedCount),
          totalCount: Math.max(Number(previous.totalCount) || 0, nextTotalCount),
        };
      });

      slowRouteDismissTimeoutRef.current = setTimeout(() => {
        hideSlowRouteOverlay();
      }, 220);
    };

    window.addEventListener(PLANNING_UPDATES_LOADER_EVENT, handlePlanningUpdatesLoader);
    const latestPlanningLoaderState =
      typeof window !== 'undefined' ? window[PLANNING_UPDATES_LOADER_STATE_KEY] : null;
    if (latestPlanningLoaderState) {
      handlePlanningUpdatesLoader({ detail: latestPlanningLoaderState });
    }

    return () => {
      if (planningLoaderFallbackTimeoutRef.current) {
        clearTimeout(planningLoaderFallbackTimeoutRef.current);
        planningLoaderFallbackTimeoutRef.current = null;
      }
      window.removeEventListener(PLANNING_UPDATES_LOADER_EVENT, handlePlanningUpdatesLoader);
    };
  }, [router.pathname]);

  useEffect(() => {
    function handler(e) {
      if (e?.detail && typeof e.detail.processing === 'boolean') {
        console.log(`_app.js: Received categorization-processing event: ${e.detail.processing}`);
        setIsCategorizationProcessing(e.detail.processing);
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('categorization-processing', handler);
      return () => window.removeEventListener('categorization-processing', handler);
    }
  }, []);

  // Use consistent background that respects theme variables
  const containerBgClass = 'bg-background'; // Always use theme background variable
  const scanlineClass = router.pathname === '/' ? '' : 'crt-scanline';
  const shouldShowBackgroundProcessingIndicator =
    !isSlowRoute(router.asPath || router.pathname);
  const planningRegionRoute = isPlanningRegionRoute(router.asPath || router.pathname);
  const hideGlobalNavigation =
    router.pathname === '/admin-dashboard' || router.pathname === '/agent-studio';

  return (
    <>
      {browserObservabilityEnabled && GA_TRACKING_ENABLED && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            strategy="afterInteractive"
          />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}');
            `}
          </Script>
        </>
      )}
      {browserObservabilityEnabled && (
        <>
          <CityScopeClientErrorTracker />
          <CityScopeJourneyTracker />
          <MatomoPagesRouteTracker />
        </>
      )}
      {shouldLoadAmplifyClient && <ConfigureAmplifyClient />}
      {!DEMO_MODE_ENABLED && <GlobalToastListener />}
      <GlobalErrorHandler />
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={true} disableTransitionOnChange>
        <SettingsStyleProvider />
        <UnifiedThemeProvider>
          <div 
            id="app-scroll-container"
            ref={scrollContainerRef} 
            className={`relative w-full min-h-screen overflow-y-auto ${containerBgClass} ${scanlineClass} ${planningRegionRoute ? 'planning-region-route' : ''}`}
          > 
            {!hideGlobalNavigation && <Navbar isVisible={isVisible} />}
            {planningRegionRoute && <PlanningAreaSelector isNavVisible={isVisible} />}
            <SlowRouteTransitionOverlay
              active={slowRouteOverlay.active}
              targetUrl={slowRouteOverlay.targetUrl}
              progress={slowRouteOverlay.progress}
              stage={slowRouteOverlay.stage}
              title={slowRouteOverlay.title}
              description={slowRouteOverlay.description}
              loadedCount={slowRouteOverlay.loadedCount}
              totalCount={slowRouteOverlay.totalCount}
            />
            <main>
              <Component {...pageProps} />
              <PlanningDocumentViewerModal />
              {browserObservabilityEnabled && <VercelAnalytics />}
              {!DEMO_MODE_ENABLED &&
                isCategorizationProcessing &&
                shouldShowBackgroundProcessingIndicator && (
                <BackgroundProcessingIndicator isProcessing={isCategorizationProcessing} />
              )}
              {showCacheStatus && (
                <IconCacheStatus 
                  show={showCacheStatus} 
                  onClose={() => setShowCacheStatus(false)}
                  position="bottom-right"
                />
              )}
            </main>
            {!DEMO_MODE_ENABLED && shouldLoadWelcomeBackModal && <WelcomeBackAccountModal />}
          </div>
        </UnifiedThemeProvider>
      </ThemeProvider>
      {!DEMO_MODE_ENABLED && <Toaster />}
      {browserObservabilityEnabled && <VercelSpeedInsights sampleRate={0.1} />}
    </>
  );
}

export default MyApp;
